import { supabase } from "@/integrations/supabase/client";
import { DigitalContent } from "@/types/digitalContent";

/**
 * Buscar conteúdos digitais públicos de um mentor específico
 */
export async function getPublicDigitalContents(mentorId: string): Promise<DigitalContent[]> {
  try {
    console.log("📚 [getPublicDigitalContents] Buscando conteúdos públicos do mentor:", mentorId);

    const { data, error } = await supabase
      .from("digital_contents")
      .select(`
        *,
        mentor:mentor_id (
          id, 
          image_url, 
          profile_id,
          profiles:profile_id (
            id,
            nickname,
            slug
          )
        ),
        game:game_id (
          id, 
          name, 
          cover_url
        )
      `)
      .eq("mentor_id", mentorId)
      .eq("is_active", true)
      .order("display_order", { ascending: true })
      .order("created_at", { ascending: false });

    if (error) {
      console.error("📚 [getPublicDigitalContents] Erro ao buscar conteúdos:", error);
      throw error;
    }

    console.log("📚 [getPublicDigitalContents] Conteúdos encontrados:", data?.length || 0);

    // Mapear dados para formato padronizado
    const mappedData = (data || []).map(mapDigitalContentData);

    return mappedData;
  } catch (error) {
    console.error("📚 [getPublicDigitalContents] Erro:", error);
    throw error;
  }
}

/**
 * Buscar todos os guias (conteúdos digitais) ativos da plataforma
 */
export async function getAllActiveDigitalContents(): Promise<DigitalContent[]> {
  try {
    const { data, error } = await supabase
      .from("digital_contents")
      .select(`
        *,
        mentor:mentor_id (
          id,
          image_url,
          profile_id,
          profiles:profile_id (
            id,
            nickname,
            slug
          )
        ),
        game:game_id (
          id,
          name,
          cover_url
        )
      `)
      .eq("is_active", true)
      .order("is_featured", { ascending: false })
      .order("display_order", { ascending: true })
      .order("created_at", { ascending: false });

    if (error) {
      console.error("📚 [getAllActiveDigitalContents] Erro ao buscar guias:", error);
      throw error;
    }

    return (data || []).map(mapDigitalContentData);
  } catch (error) {
    console.error("📚 [getAllActiveDigitalContents] Erro:", error);
    throw error;
  }
}


/**
 * Buscar um conteúdo digital específico para compra
 */
export async function getDigitalContentForPurchase(contentId: string): Promise<DigitalContent | null> {
  try {
    console.log("📚 [getDigitalContentForPurchase] Buscando conteúdo:", contentId);

    const { data, error } = await supabase
      .from("digital_contents")
      .select(`
        *,
        mentor:mentor_id (
          id, 
          image_url, 
          profile_id,
          profiles:profile_id (
            id,
            nickname,
            slug
          )
        ),
        game:game_id (
          id, 
          name, 
          cover_url
        )
      `)
      .eq("id", contentId)
      .eq("is_active", true)
      .single();

    if (error) {
      console.error("📚 [getDigitalContentForPurchase] Erro ao buscar conteúdo:", error);
      throw error;
    }

    if (!data) {
      console.log("📚 [getDigitalContentForPurchase] Conteúdo não encontrado");
      return null;
    }

    console.log("📚 [getDigitalContentForPurchase] Conteúdo encontrado:", data.title);

    return mapDigitalContentData(data);
  } catch (error) {
    console.error("📚 [getDigitalContentForPurchase] Erro:", error);
    throw error;
  }
}

// Função auxiliar para mapear dados do banco para o formato padronizado
function mapDigitalContentData(item: any): DigitalContent {
  const mentorName = item.mentor?.profiles?.nickname || 
                    `Mentor ${item.mentor_id?.substring(0, 4)}`;
  
  return {
    id: item.id,
    mentor_id: item.mentor_id,
    title: item.title,
    description: item.description,
    content_type: item.content_type,
    price_cents: item.price_cents,
    cover_image_url: item.cover_image_url,
    file_url: item.file_url,
    file_size_bytes: item.file_size_bytes,
    file_name: item.file_name,
    preview_content: item.preview_content,
    tags: item.tags,
    difficulty_level: item.difficulty_level,
    estimated_duration_minutes: item.estimated_duration_minutes,
    is_active: item.is_active,
    is_featured: item.is_featured,
    display_order: item.display_order,
    download_count: item.download_count,
    game_id: item.game_id,
    created_at: item.created_at,
    updated_at: item.updated_at,
    game: item.game,
    mentor: item.mentor,
    mentor_name: mentorName,
    mentor_image_url: item.mentor?.image_url,
    mentor_slug: item.mentor?.profiles?.slug
  };
} 